import * as React from 'react';
import { useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import StepLabel from '@mui/material/StepLabel';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { useFormik } from 'formik';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';

import {
  useRegisterSpotMutation,
  useSwitchAccountMutation,
  BillingRoleEnum,
  BillingPlanEnum,
} from '@graphql';
import { useSession, useRecaptcha } from '@hooks';
import { parseApolloError } from '@utils';
import {
  SiretAutocomplete,
  Firstname,
  Lastname,
  MobilePhone,
} from '../../../components/authentication/simple-signup/signup-form/FormFields';
import { AcceptanceCheckboxes } from '../../../components/authentication/common';
import validationSchema from '../../../components/authentication/simple-signup/signup-form/validationSchemas';
import { ContactSupport } from './ContactSupport';

interface RegisterStepperProps {}

export function RegisterStepper(props: Readonly<RegisterStepperProps>) {
  const { refetch } = useSession();
  const [activeStep, setActiveStep] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState<null | { id: string; name: string; slug: string }>(null);
  const [error, setError] = React.useState<null | string>(null);
  const [completed, setCompleted] = React.useState<{
    [k: number]: boolean;
  }>({});
  const { t } = useTranslation('cta');
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const { executeRecaptcha, isRecaptchaReady } = useRecaptcha();
  const navigate = useNavigate();

  const steps = [
    t('register.stepper.Your establishment', { ns: 'spot' }),
    t('register.stepper.Your information', { ns: 'spot' }),
    t('register.stepper.Confirmation', { ns: 'spot' }),
  ];

  const isSiretError = Boolean(
    error && ['SIRET_CONFLICT', 'SIRET_NOT_FOUND', 'SIRET_ALREADY_EXISTS'].includes(error)
  );

  const [registerSpot, { data: registerSpotData, error: registerSpotError }] = useRegisterSpotMutation();

  const [switchAccount] = useSwitchAccountMutation({
    async onCompleted() {
      await refetch();
      setLoading(false);
    },
  });

  const formik = useFormik({
    validateOnMount: true,
    initialValues: {
      siret: '',
      accountName: '',
      address: '',
      firstname: '',
      lastname: '',
      mobilePhone: '',
      role: '' as BillingRoleEnum | '',
      isAcceptedTOS: false,
    },
    initialStatus: {
      resolvedSiret: null,
    },
    validationSchema: yup.object({
      ...validationSchema('spot', t, ['siret', 'accountName', 'mobilePhone']).fields,
      address: yup.string(),
      siret: yup.string().length(14).required(),
      accountName: yup.string().when('siret', {
        is: (siret?: string) => (siret ?? '').replace(/\s/g, '').length === 14,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.notRequired(),
      }),
      role: yup.string().oneOf(Object.values(BillingRoleEnum)).required(),
      isAcceptedTOS: yup
        .boolean()
        .required()
        .oneOf([true])
        .label(t('isAcceptedTOS.validation.label', { ns: 'field' })),
    }),
    onSubmit: async (values) => {
      if (!isRecaptchaReady) {
        // reCAPTCHA script not ready yet
        import.meta.env.DEV && console.error('reCAPTCHA not ready');
        return;
      }

      const { accountName, address, ...rest } = values;
      const plan = (searchParams.get('plan') as BillingPlanEnum) ?? BillingPlanEnum.Freemium;
      const recaptchaToken = await executeRecaptcha('login');

      if (!recaptchaToken) {
        import.meta.env.DEV && console.error('Failed to get reCAPTCHA token');
        return;
      }

      registerSpot({
        variables: {
          input: {
            ...rest,
            role: values.role === '' ? BillingRoleEnum.Other : values.role,
            recaptchaToken,
            plan,
          },
        },
      });
    },
  });

  const siretIs14 = formik.values.siret.replace(/\s/g, '').length === 14;
  const canGoNextStep0 = siretIs14 && !isSiretError;
  const canGoNextStep1 =
    Boolean(formik.values.firstname.length) &&
    !Boolean(formik.errors.firstname) &&
    Boolean(formik.values.lastname.length) &&
    !Boolean(formik.errors.lastname) &&
    Boolean(formik.values.mobilePhone.length) &&
    !Boolean(formik.errors.mobilePhone);

  const totalSteps = () => {
    return steps.length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all steps have been completed,
          // find the first step that has been completed
          steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      return handleReset();
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const handleComplete = () => {
    setCompleted({
      ...completed,
      [activeStep]: true,
    });
    handleNext();
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompleted({});
    formik.resetForm({ values: formik.initialValues });
    setSearchParams({});
  };

  const handleNavigateToSpot = async () => {
    if (!success) return;
    setLoading(true);
    await switchAccount({
      variables: {
        id: success.id,
      },
    });
    await refetch();
    setLoading(false);
    navigate(`/`, { replace: true });
  };

  React.useEffect(() => {
    if (registerSpotData?.registerSpot && registerSpotData.registerSpot.__typename === 'Spot') {
      setSuccess({
        id: registerSpotData.registerSpot.id,
        name: registerSpotData.registerSpot.name,
        slug: registerSpotData.registerSpot.slug,
      });
    }

    if (registerSpotError) {
      const parsed = parseApolloError(registerSpotError);
      const message = parsed?.message ?? null;

      if (message) {
        setSuccess(null);
      }

      if (message === 'SUSPICIOUS_ACTIVITY') {
        // redirect to home page with reload
        window.location.href = '/';
      } else if (message === 'SIRET_NOT_FOUND') {
        formik.setStatus({
          ...(formik.status ?? {}),
          resolvedSiret: {
            ...(formik.status?.resolvedSiret ?? {}),
            isExists: false,
          },
        });
        setCompleted({ ...completed, 0: false });
      } else if (message === 'SIRET_ALREADY_EXISTS') {
        setCompleted({ ...completed, 0: false });
        formik.setStatus({
          ...(formik.status ?? {}),
          resolvedSiret: {
            ...(formik.status?.resolvedSiret ?? {}),
            isRegistered: true,
          },
        });
      } else {
        setError('GENERIC_ERROR');
      }
    }
  }, [registerSpotData, registerSpotError]);

  React.useEffect(() => {
    if (isSiretError && formik.values.siret.trim().length < 14) {
      setError(null);
    }
  }, [error, formik.values.siret, isSiretError, activeStep]);

  React.useEffect(() => {
    const { resolvedSiret } = formik.status || {};

    if (resolvedSiret?.isRegistered === true) {
      setError('SIRET_ALREADY_EXISTS');
    } else if (resolvedSiret?.isExists === false) {
      setError('SIRET_NOT_FOUND');
    } else if (resolvedSiret?.isConflict === true) {
      setError('SIRET_CONFLICT');
    } else if (isSiretError || (error === 'GENERIC_ERROR' && activeStep !== steps.length - 1)) {
      setError(null);
    }
  }, [formik.status, activeStep]);

  if (success?.name && success?.slug) {
    return (
      <Stack width="100%" height="100%" flex={1} maxWidth="sm" py={5} justifyContent="center" spacing={5}>
        <Typography variant="h1" fontWeight="bold" align="center" width="100%">
          🎉
        </Typography>
        <Alert severity="success">
          {t('register.alert.success.Your spot has been registered successfully!', {
            ns: 'spot',
          })}
        </Alert>
        <Typography variant="h6" fontWeight="bold" align="center" width="100%">
          {t('register.To continue to the spot XX, click on the button below.', {
            ns: 'spot',
            name: success.name,
          })}
        </Typography>
        <Button
          variant="contained"
          color="success"
          onClick={handleNavigateToSpot}
          sx={{ ml: 'auto' }}
          endIcon={<theme.icons.chevron />}
          loading={loading}
        >
          {t('go to my spot')}
        </Button>
      </Stack>
    );
  }

  return (
    <Stack
      width="100%"
      height="100%"
      flex={1}
      maxWidth="sm"
      py={5}
      justifyContent="space-between"
      spacing={5}
    >
      <Stepper nonLinear activeStep={activeStep} alternativeLabel>
        {steps.map((label, index) => (
          <Step key={label} completed={completed[index]}>
            <StepButton onClick={handleStep(index)}>
              <StepLabel
                sx={{
                  '& .MuiStepIcon-root.Mui-completed': {
                    color: 'success.main',
                  },
                }}
              >
                {label}
              </StepLabel>
            </StepButton>
          </Step>
        ))}
      </Stepper>

      <>
        {activeStep === 0 && (
          <Stack spacing={2}>
            <SiretAutocomplete formik={formik} />
            <Stack
              height={100}
              alignItems="center"
              justifyContent="center"
              pt={2}
              spacing={2}
              sx={
                isSiretError
                  ? {
                      backgroundColor: 'rgba(156, 151, 0, 0.1)',
                      border: '1px solid rgba(192, 195, 0, 0.5)',
                      borderRadius: 1,
                      p: 2,
                    }
                  : undefined
              }
            >
              {isSiretError && (
                <Typography variant="body2" color="text.primary">
                  {t('register.Any problem with your SIRET number?', { ns: 'spot' })}
                </Typography>
              )}
              <ContactSupport variant="contained" siret={formik.values.siret} display={isSiretError} />
            </Stack>
            {Boolean(formik.values.accountName.length) && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Typography>{t('register.Name of the establishment', { ns: 'spot' })}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formik.values.accountName}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
        {activeStep === 1 && (
          <Stack spacing={2}>
            <Firstname formik={formik} />
            <Lastname formik={formik} />
            <MobilePhone formik={formik} />
            <FormControl sx={{ m: 1, minWidth: 80 }}>
              <InputLabel id="role-label">{t('role.title', { ns: 'billing' })}</InputLabel>
              <Select
                labelId="role-label"
                id="role-select"
                name="role"
                label={t('role.title', { ns: 'billing' })}
                onChange={formik.handleChange}
                value={formik.values.role}
              >
                <MenuItem value="">
                  <em>{t('Choose')}</em>
                </MenuItem>
                <MenuItem value={BillingRoleEnum.Owner}>
                  {t(`role.${BillingRoleEnum.Owner}`, { ns: 'billing' })}
                </MenuItem>
                <MenuItem value={BillingRoleEnum.Director}>
                  {t(`role.${BillingRoleEnum.Director}`, { ns: 'billing' })}
                </MenuItem>
                <MenuItem value={BillingRoleEnum.Manager}>
                  {t(`role.${BillingRoleEnum.Manager}`, { ns: 'billing' })}
                </MenuItem>
                <MenuItem value={BillingRoleEnum.Employee}>
                  {t(`role.${BillingRoleEnum.Employee}`, { ns: 'billing' })}
                </MenuItem>
                <MenuItem value={BillingRoleEnum.Other}>
                  {t(`role.${BillingRoleEnum.Other}`, { ns: 'billing' })}
                </MenuItem>
              </Select>
            </FormControl>
          </Stack>
        )}
        {activeStep === 2 && (
          <Stack spacing={5}>
            <Alert severity={!!error ? 'error' : 'info'}>
              {error
                ? t(`register.alert.error.${error}`, { ns: 'spot' })
                : t(
                    'register.alert.info.Please confirm that your information are correct before submitting',
                    { ns: 'spot' }
                  )}
            </Alert>
            <Stack spacing={1}>
              <Typography
                variant="h6"
                sx={{
                  textTransform: 'uppercase',
                }}
              >
                {t('register.My spot', { ns: 'spot' })}
              </Typography>
              <Stack
                p={1}
                spacing={1}
                divider={<Divider flexItem />}
                sx={{
                  border: isSiretError
                    ? '1px solid rgba(195, 0, 0, 0.5)'
                    : '1px solid rgba(255, 255, 255, .2)',
                  borderRadius: 1,
                  backgroundColor: isSiretError ? 'rgba(156, 0, 0, 0.1)' : 'background.paper',
                }}
              >
                <Stack direction="row" spacing={2} justifyContent="space-between">
                  <Typography variant="body2">{t('word.Name', { ns: 'common' })}</Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={error === 'SIRET_NOT_FOUND' ? 'error.main' : 'inherit'}
                  >
                    {formik.values.accountName.length ? formik.values.accountName : '-'}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} justifyContent="space-between">
                  <Typography variant="body2">Siret</Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color={error === 'SIRET_NOT_FOUND' ? 'error.main' : 'inherit'}
                  >
                    {formik.values.siret.length ? formik.values.siret : '-'}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            <Stack spacing={1}>
              <Typography
                variant="h6"
                sx={{
                  textTransform: 'uppercase',
                }}
              >
                {t('register.My contact information', { ns: 'spot' })}
              </Typography>
              <Stack
                p={1}
                spacing={1}
                divider={<Divider flexItem />}
                sx={{
                  border: '1px solid rgba(255, 255, 255, .2)',
                  borderRadius: 1,
                  backgroundColor: 'background.paper',
                }}
              >
                <Stack direction="row" spacing={2} justifyContent="space-between">
                  <Typography variant="body2">{t('firstname.label', { ns: 'field' })}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formik.values.firstname.length ? formik.values.firstname : '-'}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} justifyContent="space-between">
                  <Typography variant="body2">{t('lastname.label', { ns: 'field' })}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formik.values.lastname.length ? formik.values.lastname : '-'}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} justifyContent="space-between">
                  <Typography variant="body2">{t('mobilePhone.label', { ns: 'field' })}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formik.values.mobilePhone.length ? formik.values.mobilePhone : '-'}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={2} justifyContent="space-between">
                  <Typography variant="body2">{t('role.title', { ns: 'billing' })}</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formik.values.role?.length ? t(`role.${formik.values.role}`, { ns: 'billing' }) : '-'}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            <AcceptanceCheckboxes
              formik={formik}
              displayPrivacyPolicy={false}
              displayNewsletter={false}
              disabled={!!error}
            />
          </Stack>
        )}
      </>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button color="uncolored" variant="outlined" onClick={handleBack} sx={{ mr: 1 }}>
          {activeStep === 0 ? t('back to plans') : t('back')}
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            color="success"
            disabled={activeStep === 0 ? !canGoNextStep0 : activeStep === 1 ? !canGoNextStep1 : true}
            onClick={handleComplete}
            sx={{ mr: 1 }}
          >
            {t('next')}
          </Button>
        ) : (
          <Button
            variant="contained"
            type="button"
            onClick={formik.handleSubmit}
            disabled={!formik.isValid || !!error}
            color="success"
            sx={{ ml: 'auto' }}
            startIcon={<theme.icons.rocket />}
          >
            {t("let's go", { ns: 'cta' })}
          </Button>
        )}
      </Stack>
    </Stack>
  );
}
